// Change Password Modal — authenticated users only

import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FiX, FiLock, FiEye, FiEyeOff, FiCheck } from 'react-icons/fi';
import { resetAuth } from '../../redux/slices/authSlice';
import authApi from '../../api/authApi';

const ChangePasswordModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [show, setShow] = useState({ current: false, new: false, confirm: false });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '', general: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.currentPassword) e.currentPassword = 'Current password is required';
    if (form.newPassword.length < 8) e.newPassword = 'New password must be at least 8 characters';
    else if (form.newPassword === form.currentPassword) e.newPassword = 'New password must be different from current password';
    if (form.newPassword !== form.confirmPassword) e.confirmPassword = 'Passwords do not match';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await authApi.changePassword(form.currentPassword, form.newPassword);
      setSuccess(true);
      setTimeout(() => {
        dispatch(resetAuth());
        navigate('/login');
      }, 2000);
    } catch (err) {
      const errMsg = err?.error || 'Failed to change password';
      if (errMsg.toLowerCase().includes('incorrect') || errMsg.toLowerCase().includes('current password')) {
        setErrors({ currentPassword: errMsg });
      } else if (errMsg.toLowerCase().includes('different')) {
        setErrors({ newPassword: errMsg });
      } else if (errMsg.toLowerCase().includes('8 characters')) {
        setErrors({ newPassword: errMsg });
      } else {
        setErrors({ general: errMsg });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setErrors({});
    setSuccess(false);
    onClose();
  };

  const fields = [
    { key: 'currentPassword', label: 'Current password', showKey: 'current', placeholder: 'Enter current password' },
    { key: 'newPassword', label: 'New password', showKey: 'new', placeholder: 'Min. 8 characters' },
    { key: 'confirmPassword', label: 'Confirm new password', showKey: 'confirm', placeholder: 'Confirm your new password' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl animate-fade-in-up">

        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Change Password</h2>
            <p className="text-slate-400 text-sm mt-0.5">Update your admin account password</p>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-40"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {success ? (
            <div className="py-6 text-center space-y-3">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                <FiCheck className="w-8 h-8 text-emerald-600" />
              </div>
              <p className="font-semibold text-slate-900">Password changed successfully!</p>
              <p className="text-slate-500 text-sm">Redirecting to login...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {errors.general && (
                <div className="px-4 py-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm font-medium">
                  {errors.general}
                </div>
              )}

              {fields.map(({ key, label, showKey, placeholder }) => (
                <div key={key}>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    {label} <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none" />
                    <input
                      type={show[showKey] ? 'text' : 'password'}
                      value={form[key]}
                      onChange={(e) => handleChange(key, e.target.value)}
                      placeholder={placeholder}
                      disabled={loading}
                      className={`w-full pl-11 pr-12 py-3 border rounded-xl text-slate-800 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 transition-all disabled:opacity-60 ${
                        errors[key]
                          ? 'border-rose-400 bg-rose-50 focus:ring-rose-300'
                          : 'border-slate-200 focus:ring-violet-300 focus:border-violet-400'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShow((p) => ({ ...p, [showKey]: !p[showKey] }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {show[showKey] ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors[key] && (
                    <p className="text-rose-500 text-xs mt-1.5 font-medium">{errors[key]}</p>
                  )}
                </div>
              ))}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="flex-1 py-3 px-4 rounded-xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 transition-all disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-3 px-4 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-semibold text-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading && <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                  {loading ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
